import { memo, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import {
  appendQueryParamsToUrl,
  formatCustomUrlPublicPreview,
} from "./apiServiceApiHelpers";
import { K, apiLabel } from "./apiServiceI18n";

let paramRowId = 0;
const nextId = () => ++paramRowId;

const HEADER_BG = "#5f6878";
const GRID_BORDER = "1px solid #c5c9d0";

const gridCellInputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "#c5c9d0" },
  },
  "& .MuiOutlinedInput-input": { py: 0.75, px: 1 },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
};

function emptyParamRow() {
  return { id: nextId(), name: "", value: "" };
}

function normalizeParamRows(params) {
  const list = Array.isArray(params) ? params : [];
  if (!list.length) return [emptyParamRow()];
  return list.map((r) => ({ ...r, id: r.id ?? nextId() }));
}

function cleanParams(rows) {
  return rows
    .filter((r) => (r.name || "").trim() || (r.value || "").trim())
    .map(({ name, value: val }) => ({ name: name.trim(), value: (val ?? "").trim() }));
}

function CustomUrlDrawer({
  open,
  onClose,
  value,
  baseUrlTo = "",
  onApply,
  applyLoading = false,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const [from, setFrom] = useState("");
  const [rows, setRows] = useState([emptyParamRow()]);
  const [applyError, setApplyError] = useState("");

  useEffect(() => {
    if (!open) return;
    setFrom(value?.from ?? "");
    setRows(normalizeParamRows(value?.params));
    setApplyError("");
  }, [open, value]);

  const cleanedParams = useMemo(() => cleanParams(rows), [rows]);

  const toDisplay = useMemo(
    () => appendQueryParamsToUrl(baseUrlTo, cleanedParams),
    [baseUrlTo, cleanedParams]
  );

  const publicPreview = useMemo(() => formatCustomUrlPublicPreview(from), [from]);

  const hasParamsWithoutPath = !from.trim() && cleanedParams.length > 0;

  const addRow = () => setRows((r) => [...r, emptyParamRow()]);

  const removeRow = (id) => {
    setRows((r) => {
      const next = r.filter((row) => row.id !== id);
      return next.length ? next : [emptyParamRow()];
    });
  };

  const setRow = (id, field, v) =>
    setRows((list) => list.map((row) => (row.id === id ? { ...row, [field]: v } : row)));

  const handleClearAll = () => {
    setFrom("");
    setRows([emptyParamRow()]);
    setApplyError("");
  };

  const handleApply = async () => {
    setApplyError("");

    if (hasParamsWithoutPath) {
      setApplyError(t(K.API_SVC_CUSTOM_URL_QUERY_WITHOUT_PATH));
      return;
    }

    const payload = { from: from.trim(), params: cleanedParams };
    const result = await onApply(payload);

    if (result === false) return;

    if (result && typeof result === "object" && result.ok === false) {
      setApplyError(result.message || t(K.API_SVC_CUSTOM_URL_INVALID));
      return;
    }

    onClose();
  };

  const actionButtonSx = {
    bgcolor: HEADER_BG,
    color: "#fff",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { bgcolor: "#4d5563", boxShadow: "none" },
    "&.Mui-disabled": { bgcolor: "#9aa3b0", color: "#fff" },
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 500 },
          maxWidth: "100vw",
          bgcolor: "#eef0f3",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}>
          {t(K.API_SVC_CUSTOM_URL_DRAWER)}
        </Typography>

        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <TextField
            label={apiLabel(K.LBL_FROM)}
            placeholder={t(K.API_SVC_CUSTOM_URL_FROM_PLACEHOLDER)}
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setApplyError("");
            }}
            fullWidth
            size="small"
            disabled={readOnly}
            sx={fieldSx}
            helperText={
              publicPreview
                ? t(K.API_SVC_CUSTOM_URL_PUBLIC_PREVIEW, { url: publicPreview })
                : t(K.API_SVC_CUSTOM_URL_FROM_HELP)
            }
          />

          <TextField
            label={apiLabel(K.LBL_TO)}
            value={toDisplay}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={fieldSx}
            helperText={t(K.API_SVC_CUSTOM_URL_TO_HELP)}
          />

          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 0.75, color: "text.secondary", fontSize: "0.75rem" }}
            >
              {t(K.API_SVC_ADDITIONAL_PARAMS)}
            </Typography>

            <Box
              sx={{
                border: GRID_BORDER,
                overflow: "hidden",
                bgcolor: "#fff",
                flex: 1,
                minHeight: 120,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "44px minmax(0, 1fr) minmax(0, 1fr)",
                  bgcolor: HEADER_BG,
                  color: "#fff",
                  minHeight: 36,
                  alignItems: "stretch",
                }}
              >
                <Box sx={{ borderRight: "1px solid rgba(255,255,255,0.25)" }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1,
                    borderRight: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
                    {apiLabel(K.LBL_NAME)}
                  </Typography>
                  {!readOnly && (
                    <IconButton
                      size="small"
                      onClick={addRow}
                      aria-label={t(K.API_SVC_ADD_PARAM)}
                      sx={{
                        color: "#fff",
                        p: 0.25,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
                    {apiLabel(K.LBL_VALUE)}
                  </Typography>
                </Box>
              </Box>

              {rows.map((row, index) => (
                <Box
                  key={row.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "44px minmax(0, 1fr) minmax(0, 1fr)",
                    borderTop: GRID_BORDER,
                    minHeight: 44,
                    alignItems: "stretch",
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRight: GRID_BORDER,
                    }}
                  >
                    {!readOnly && (
                      <IconButton
                        size="small"
                        onClick={() => removeRow(row.id)}
                        aria-label={t(K.API_SVC_DELETE_PARAM)}
                        sx={{ color: "text.secondary" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Box
                    sx={{
                      p: 0.75,
                      borderRight: GRID_BORDER,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={row.name}
                      onChange={(e) => setRow(row.id, "name", e.target.value)}
                      placeholder={index === 0 ? t(K.API_SVC_PARAM_NAME_PLACEHOLDER) : ""}
                      disabled={readOnly}
                      sx={gridCellInputSx}
                    />
                  </Box>
                  <Box sx={{ p: 0.75, display: "flex", alignItems: "center" }}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      value={row.value}
                      onChange={(e) => setRow(row.id, "value", e.target.value)}
                      placeholder={index === 0 ? t(K.API_SVC_PARAM_VALUE_PLACEHOLDER) : ""}
                      disabled={readOnly}
                      sx={gridCellInputSx}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {applyError && (
            <Alert severity="error" onClose={() => setApplyError("")}>
              {applyError}
            </Alert>
          )}

          {hasParamsWithoutPath && !applyError && (
            <Alert severity="warning">{t(K.API_SVC_CUSTOM_URL_QUERY_WITHOUT_PATH)}</Alert>
          )}
        </Stack>

        {!readOnly && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ pt: 2, mt: "auto" }}
          >
            <Button variant="contained" onClick={handleClearAll} sx={actionButtonSx}>
              {t(K.API_SVC_CLEAR_ALL)}
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={applyLoading || hasParamsWithoutPath}
                startIcon={applyLoading ? <CircularProgress size={14} color="inherit" /> : null}
                sx={actionButtonSx}
              >
                {applyLoading ? t(K.API_SVC_VALIDATING_ELLIPSIS) : t(K.BTN_APPLY)}
              </Button>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={applyLoading}
                sx={{ textTransform: "none", bgcolor: "#fff" }}
              >
                {t(K.BTN_CANCEL)}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

export default memo(CustomUrlDrawer);
